import {
  boundingBox,
  parsePointsAttr,
  parseStraightPathD,
  PathParseError,
  type Point,
} from '../core/paint/polygon.js';
import { fetchSvgText } from '../content/svg.js';

/**
 * Reading a vehicle template out of its SVG.
 *
 * The SVG *is* the template - art, paint regions, wheel groups, attachment anchors and the physics
 * hull, in one file that stays editable in Inkscape. See docs/ART_PIPELINE.md for the contract.
 *
 * Parsing is split from fetching so tests can hand in SVG text read from disk, and so a future
 * content pack loader can parse from anywhere.
 */

export interface Anchor {
  readonly cx: number;
  readonly cy: number;
  readonly r: number;
}

/** An axis-aligned box in template coordinates. */
export interface Box {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface VehicleTemplate {
  readonly id: string;
  /** Live element, ready to be inserted into the document. */
  readonly svg: SVGSVGElement;
  readonly width: number;
  readonly height: number;
  /**
   * The art scale: template pixels per world metre. Declared per template because vehicles get
   * traced at whatever size suits the screenshot, and a 4.5m truck and a 2m quad bike can easily end
   * up the same number of pixels wide. Distinct from PX_PER_METRE, which is the *screen* scale.
   */
  readonly pxPerMetre: number;
  /** Every paintable region id, in document order. */
  readonly regionIds: readonly string[];
  readonly wheels: {
    readonly front: Anchor | null;
    readonly rear: Anchor | null;
  };
  readonly seat: Anchor | null;
  /**
   * Where the trailer's pin goes, if the template says.
   *
   * Optional, unlike every other anchor. The trailer is always hitched, but the eight templates that
   * shipped before it have no `#anchor-hitch`, and requiring one would have failed the build on the
   * commit that added the trailer. `vehicleGeometry` derives a pin from the rear bumper when this is
   * null, so a template gains nothing but precision by carrying one. `scripts/check-vehicles.mjs`
   * validates it when it is there - behind the rear axle, and not on too long a lever.
   */
  readonly hitch: Anchor | null;
  /**
   * The glass, as a box in template coordinates.
   *
   * The pup rides behind it and is clipped to it, so this is what decides how big she is drawn and how
   * much of her shows. Measured from the shapes inside `#window` rather than from the group, because a
   * `<g>` has no geometry of its own. Null for a template with no window - she then simply is not clipped.
   */
  readonly windowBox: Box | null;
  /** Physics hull in template coordinates. Empty if absent or unparseable (M1 does not need it). */
  readonly chassis: readonly Point[];
  /** Problems that do not prevent colouring. `check:vehicles` treats these as build failures. */
  readonly warnings: readonly string[];
}

export const REGION_PREFIX = 'region-';

/** Only used when a template forgets to declare its scale; see readPxPerMetre. */
const DEFAULT_PX_PER_METRE = 100;

export function parseTemplate(svgText: string, id: string): VehicleTemplate {
  const parsed = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const error = parsed.querySelector('parsererror');
  if (error !== null) {
    throw new Error(`template "${id}" is not valid SVG: ${error.textContent ?? 'unknown error'}`);
  }

  const svg = parsed.documentElement as unknown as SVGSVGElement;
  if (svg.tagName.toLowerCase() !== 'svg') {
    throw new Error(`template "${id}" root element is <${svg.tagName}>, expected <svg>`);
  }

  const warnings: string[] = [];
  const { width, height } = readViewBox(svg, warnings);
  const pxPerMetre = readPxPerMetre(svg, warnings);

  const regionIds = [...svg.querySelectorAll(`[id^="${REGION_PREFIX}"]`)].map((node) => node.id);
  if (regionIds.length === 0) warnings.push('no region-* elements: nothing can be coloured');
  const duplicates = findDuplicates(regionIds);
  if (duplicates.length > 0) warnings.push(`duplicate region ids: ${duplicates.join(', ')}`);

  // Line art sits on top of the fills, so without this a tap on a thick outline never reaches the
  // region underneath. Belt and braces - the SVG sets it too, but a hand-traced file will forget.
  for (const node of svg.querySelectorAll('#lines, #meta, .lines')) {
    node.setAttribute('pointer-events', 'none');
  }

  return {
    id,
    svg,
    width,
    height,
    pxPerMetre,
    regionIds,
    wheels: {
      front: readAnchor(svg, 'anchor-wheel-front', warnings),
      rear: readAnchor(svg, 'anchor-wheel-rear', warnings),
    },
    seat: readAnchor(svg, 'anchor-seat', warnings),
    // No warning when it is absent: absence is the normal case, and a warning per template per drive
    // would drown the ones that mean something.
    hitch: anchorOf(svg, 'anchor-hitch'),
    windowBox: readWindowBox(svg, warnings),
    chassis: readChassis(svg, warnings),
    warnings,
  };
}

/**
 * A template, fetched and measured.
 *
 * The *text* is cached - `src/content/svg.ts` owns that cache, and the rest of the pack already goes
 * through it - but the parse happens every time on purpose. A `VehicleTemplate` holds a live SVG element
 * that the paint surface then colours in, so handing the same one to two callers would have her drawing
 * appear on the picker's thumbnail. What this saves is the eight fetches the chooser fires on the way in,
 * every time she visits it, and the ninth for whichever car she then picks.
 */
export async function fetchTemplate(url: string, id: string): Promise<VehicleTemplate> {
  let text;
  try {
    text = await fetchSvgText(url);
  } catch (error) {
    throw new Error(`could not load template "${id}" from ${url}`, { cause: error });
  }
  return parseTemplate(text, id);
}

/** Where the vehicle art lives for a given template id. */
export function templateUrl(id: string, pack = 'patrol'): string {
  return `./content/packs/${pack}/vehicles/${id}.svg`;
}

/*
 * The measurements a caller who only wants to *place something on* the art needs, for a caller that
 * already holds a parsed SVG rather than its text.
 *
 * The Lookout draws her pup in the cab of the car on its big badge, and needs exactly the pane and the
 * coordinate space it is measured in - not regions, not a physics hull, and not the warnings, which are a
 * build-time concern. Thin wrappers rather than a second measurement: `readWindowBox` handles a windscreen
 * that is a slanted path, and a screen that guessed at a box instead would put the pup somewhere else than
 * the drive scene does.
 */

/** The glass, in template coordinates, or null if this art has none that can be measured. */
export function windowBoxOf(svg: SVGSVGElement): Box | null {
  return readWindowBox(svg, []);
}

/**
 * One named region's own box, in template coordinates. Null for a template that has no such region.
 *
 * The same question `anchorOf` answers - one named thing off the art, for a caller that wants to put
 * something on top of it - and the emergency lights are what asks it: the drive scene places an additive
 * glow from `region-lightbar`'s or `region-beacon`'s own bounding box, so the art needs no new anchor.
 *
 * Measured with the same crude scan `readWindowBox` uses, deliberately. This is the box the glow will
 * actually be placed from, so measuring it more precisely here than `check:vehicles` does would pass
 * files that the game then gets visibly wrong.
 *
 * Not a field on `VehicleTemplate`, for three reasons. A missing `#window` is a real defect and pushes a
 * warning; four of the eight vehicles legitimately have no light fixture at all, and a warnings list
 * that complains a hovercraft has no light bar is a warnings list everyone learns to ignore.
 * `parseTemplate` is also used for pup art, where such a field would be noise on half its instances. And
 * the *presence* test is already free from the parsed `regionIds`, so only the box needs measuring.
 */
export function regionBoxOf(svg: SVGSVGElement, id: string): Box | null {
  const node = svg.querySelector(`#${id}`);
  if (node === null) return null;

  const box = measure(node);
  if (box === null) return null;
  return { x: box.x0, y: box.y0, width: box.x1 - box.x0, height: box.y1 - box.y0 };
}

/** The coordinate space the rest of the numbers are in. */
export function viewBoxOf(svg: SVGSVGElement): {
  readonly width: number;
  readonly height: number;
} {
  return readViewBox(svg, []);
}

/**
 * One named anchor off any piece of pack art, vehicle or pup.
 *
 * The convention - a `<circle>` in `#meta` whose radius carries the target size - belongs to the pack
 * rather than to vehicles (see docs/ART_PIPELINE.md), and both the pup rig and the Lookout read pup
 * anchors with it. Null for anything malformed, which every caller already treats as "no anchor".
 */
export function anchorOf(svg: SVGSVGElement, id: string): Anchor | null {
  return readAnchor(svg, id, []);
}

/**
 * Read `data-px-per-metre` from #meta (or the root). Falls back to the screen scale with a warning
 * rather than throwing, so a partly-traced template still loads - it will just be the wrong size,
 * which is obvious the moment you look at it.
 */
function readPxPerMetre(svg: SVGSVGElement, warnings: string[]): number {
  const source =
    svg.querySelector('#meta')?.getAttribute('data-px-per-metre') ??
    svg.getAttribute('data-px-per-metre');

  if (source === null) {
    warnings.push('no data-px-per-metre on #meta: falling back to the screen scale');
    return DEFAULT_PX_PER_METRE;
  }

  const value = Number(source);
  if (!Number.isFinite(value) || value <= 0) {
    warnings.push(`data-px-per-metre "${source}" is not a positive number`);
    return DEFAULT_PX_PER_METRE;
  }
  return value;
}

function readViewBox(
  svg: SVGSVGElement,
  warnings: string[],
): { readonly width: number; readonly height: number } {
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox !== null) {
    const parts = viewBox.trim().split(/[\s,]+/).map(Number);
    const [, , w, h] = parts;
    if (parts.length === 4 && Number.isFinite(w) && Number.isFinite(h)) {
      return { width: w as number, height: h as number };
    }
    warnings.push(`unparseable viewBox "${viewBox}"`);
  } else {
    warnings.push('no viewBox: the template will not scale to the screen');
  }

  const width = Number(svg.getAttribute('width') ?? 1024);
  const height = Number(svg.getAttribute('height') ?? 640);
  return {
    width: Number.isFinite(width) ? width : 1024,
    height: Number.isFinite(height) ? height : 640,
  };
}

/** Anchors are authored as circles: centre plus a radius that carries the target size. */
/**
 * The union of the paintable shapes inside `#window`.
 *
 * Only rect/circle/ellipse are measured exactly; anything else falls back to the numbers in its `d` or
 * `points`, which over-estimates a curve's box slightly. That is the right way to be wrong here: a
 * marginally generous clip shows a sliver too much pup, where a mean one would cut off her ears.
 */
function readWindowBox(svg: SVGSVGElement, warnings: string[]): Box | null {
  const group = svg.querySelector('#window');
  if (group === null) {
    warnings.push('missing #window: the pup would be drawn on the bodywork instead of behind glass');
    return null;
  }

  let box: { x0: number; y0: number; x1: number; y1: number } | null = null;
  for (const node of group.querySelectorAll(`[id^="${REGION_PREFIX}"]`)) {
    const next = measure(node);
    if (next === null) continue;
    box =
      box === null
        ? next
        : {
            x0: Math.min(box.x0, next.x0),
            y0: Math.min(box.y0, next.y0),
            x1: Math.max(box.x1, next.x1),
            y1: Math.max(box.y1, next.y1),
          };
  }

  if (box === null) {
    warnings.push('#window has no region-* shapes, so its size cannot be measured');
    return null;
  }
  return { x: box.x0, y: box.y0, width: box.x1 - box.x0, height: box.y1 - box.y0 };
}

function measure(node: Element): { x0: number; y0: number; x1: number; y1: number } | null {
  const num = (name: string): number => Number(node.getAttribute(name));
  const tag = node.tagName.toLowerCase();

  if (tag === 'rect') {
    const [x, y, w, h] = [num('x'), num('y'), num('width'), num('height')];
    if (![x, y, w, h].every(Number.isFinite)) return null;
    return { x0: x, y0: y, x1: x + w, y1: y + h };
  }
  if (tag === 'circle' || tag === 'ellipse') {
    const cx = num('cx');
    const cy = num('cy');
    const rx = tag === 'circle' ? num('r') : num('rx');
    const ry = tag === 'circle' ? num('r') : num('ry');
    if (![cx, cy, rx, ry].every(Number.isFinite)) return null;
    return { x0: cx - rx, y0: cy - ry, x1: cx + rx, y1: cy + ry };
  }

  const source = node.getAttribute('points') ?? node.getAttribute('d');
  if (source === null) return null;
  const numbers = (source.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) ?? []).map(Number);
  if (numbers.length < 4) return null;

  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let i = 0; i + 1 < numbers.length; i += 2) {
    x0 = Math.min(x0, numbers[i]!);
    x1 = Math.max(x1, numbers[i]!);
    y0 = Math.min(y0, numbers[i + 1]!);
    y1 = Math.max(y1, numbers[i + 1]!);
  }
  return { x0, y0, x1, y1 };
}

function readAnchor(svg: SVGSVGElement, id: string, warnings: string[]): Anchor | null {
  const node = svg.querySelector(`#${id}`);
  if (node === null) {
    warnings.push(`missing #${id}`);
    return null;
  }

  const cx = Number(node.getAttribute('cx'));
  const cy = Number(node.getAttribute('cy'));
  const r = Number(node.getAttribute('r'));
  if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(r) || r <= 0) {
    warnings.push(`#${id} must be a <circle> with numeric cx, cy and a positive r`);
    return null;
  }
  return { cx, cy, r };
}

function readChassis(svg: SVGSVGElement, warnings: string[]): readonly Point[] {
  const node = svg.querySelector('#chassis');
  if (node === null) {
    warnings.push('missing #chassis: the physics hull must be authored, not derived');
    return [];
  }

  try {
    const points = node.getAttribute('points');
    if (points !== null) return parsePointsAttr(points);

    const d = node.getAttribute('d');
    if (d !== null) return parseStraightPathD(d);

    warnings.push('#chassis has neither a points nor a d attribute');
    return [];
  } catch (error) {
    const reason = error instanceof PathParseError ? error.message : String(error);
    warnings.push(`#chassis could not be parsed: ${reason}`);
    return [];
  }
}

/** Approximate bounds of a shape, good enough to flag un-tappably small regions. */
export function approximateBounds(node: Element): {
  readonly width: number;
  readonly height: number;
} | null {
  const tag = node.tagName.toLowerCase();

  if (tag === 'rect') {
    const w = Number(node.getAttribute('width'));
    const h = Number(node.getAttribute('height'));
    return Number.isFinite(w) && Number.isFinite(h) ? { width: w, height: h } : null;
  }
  if (tag === 'circle') {
    const r = Number(node.getAttribute('r'));
    return Number.isFinite(r) ? { width: r * 2, height: r * 2 } : null;
  }
  if (tag === 'ellipse') {
    const rx = Number(node.getAttribute('rx'));
    const ry = Number(node.getAttribute('ry'));
    return Number.isFinite(rx) && Number.isFinite(ry) ? { width: rx * 2, height: ry * 2 } : null;
  }

  // polygon/polyline/path: derive from the coordinates present in the attribute. Approximate for
  // curves (control points can lie outside the drawn shape), which is fine for a lower-bound check.
  const source = node.getAttribute('points') ?? node.getAttribute('d');
  if (source === null) return null;

  const numbers = (source.match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi) ?? []).map(Number);
  const points: Point[] = [];
  for (let i = 0; i + 1 < numbers.length; i += 2) {
    points.push({ x: numbers[i] as number, y: numbers[i + 1] as number });
  }

  const box = boundingBox(points);
  return box === null ? null : { width: box.maxX - box.minX, height: box.maxY - box.minY };
}

function findDuplicates(values: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) duplicates.add(value);
    seen.add(value);
  }
  return [...duplicates];
}
